import React from 'react'
import {
  Typography,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider
} from 'antd'
import IShelter from '../../../../interfaces/IShelter'
import ILanguage from '../../../../interfaces/ILanguage'
import { SearchRequest } from '../../../../services/api/interfaces/ApiRequest'

type ShelterAdoptionPathProps = {
  form: any
  shelter: IShelter | null
  languages: ILanguage[] | null
  contentLoading: boolean
  loadLanguages: (args: SearchRequest) => void
}

export default class ShelterAdoptionPath extends React.Component<ShelterAdoptionPathProps> {
  static defaultProps = {
    contentLoading: false
  }

  state = {
    selectedLanguages: {
      both: [...[this.props.shelter?.country?.default_language?.uuid || []]] as string[],
      remote: [...[this.props.shelter?.country?.default_language?.uuid || []]] as string[],
      in_person: [...[this.props.shelter?.country?.default_language?.uuid || []]] as string[],
    }
  }

  // Select field names, for translations. Each sits under the field key (both, remote, in_person)
  private ADOPTION_PATH_MESSAGE_BOTH = ['shelter_setting', 'adoption_path', 'both', 'translation']
  private ADOPTION_PATH_MESSAGE_REMOTE = ['shelter_setting', 'adoption_path', 'remote', 'translation']
  private ADOPTION_PATH_MESSAGE_IN_PERSON = ['shelter_setting', 'adoption_path', 'in_person', 'translation']

  componentDidMount() {
    const { languages, contentLoading, loadLanguages } = this.props

    if (!contentLoading && !languages) {
      loadLanguages({
        options: {
          limit:9999
        }
      })
    }
  }

  handleSelectLanguageChange = (name: any[], value: string, fieldPath: string[]) => {
    const { form } = this.props

    form.setFields([
      {
        name: [...fieldPath, ...name],
        value
      }
    ])

    const translationKey = fieldPath[fieldPath.length - 2]
    const currentLanguages = this.state.selectedLanguages
    // We only update the field language, removing the selected language from the list of that field
    const updatedLanguagesList = currentLanguages[translationKey][name[0] + 1] = value
    
    this.setState(state => ({
      ...state,
      selectLanguages: {
        ...currentLanguages,
        [translationKey]: updatedLanguagesList
      }
    }))
  }

  selectLanguage = (fieldName: number, fieldPath: string[]) => {
    const { form, contentLoading } = this.props
    const { selectedLanguages } = this.state
    const translationKey = fieldPath[fieldPath.length - 2]
    let languages = this.props.languages || []

    const name: any[] = [fieldName, 'language']

    const value = form.getFieldValue([...fieldPath, ...name])
    // Filter by translation field
    languages = languages.filter(lang => !selectedLanguages[translationKey].includes(lang.uuid) || lang.uuid === value)

    return (
      <Select value={value} loading={contentLoading} onChange={(value: any) => this.handleSelectLanguageChange(name, value, fieldPath)} style={{ width: 150 }}>
        {
          languages.map(lang => (
            <Select.Option key={lang.uuid} value={lang.uuid}>{lang.name} ({lang.abbreviation})</Select.Option>
          ))
        }
      </Select>
    )
  }

  removeLanguage(key: string, language?: string) {
    if (language) {
      this.setState((state: any) => ({
        ...state,
        selectedLanguages: {
          ...state.selectedLanguages,
          [key]: state.selectedLanguages[key].filter(p => p !== language)
        }
      }))
    }
  }

  render() {
    const { form } = this.props

    return (
      <>
        <Typography.Title level={5}>Adoption Path Messages</Typography.Title>
        
        <Row>
          <Col xs={24} sm={18} md={18}>
            <Form.Item
              label="Adoption Path value set to Both"
              name={['shelter_setting', 'adoption_path', 'both', 'message']}
            >
              <Input addonAfter="Default Language" />
            </Form.Item>

            <Form.List name={[...this.ADOPTION_PATH_MESSAGE_BOTH]}>
              {(fields, { add, remove }) => (
                <>
                  {
                    fields.map(field => {
                      const language = form.getFieldValue([...this.ADOPTION_PATH_MESSAGE_BOTH, field.name, 'language'])

                      return (
                        <div key={field.key} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end'
                        }}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'value']}
                            rules={[
                              {
                                required: true,
                                message: 'Required field'
                              }
                            ]}
                            style={{
                              width: '100%',
                              marginBottom: 0
                            }}
                          >
                            <Input disabled={!language} addonAfter={this.selectLanguage(field.name, this.ADOPTION_PATH_MESSAGE_BOTH)} />
                          </Form.Item>

                          <Button type="link" onClick={() => {
                            remove(field.name)

                            this.removeLanguage('both', language)
                          }}>
                            Remove
                          </Button>
                        </div>
                      )
                    })
                  }
                  <Button type="link" onClick={add}>
                    Add translation
                  </Button>
                </>
              )}
            </Form.List>

            <Divider />

            <Form.Item
              label="Adoption Path value set to Remote"
              name={['shelter_setting', 'adoption_path', 'remote', 'message']}
            >
              <Input addonAfter="Default Language" />
            </Form.Item>

            <Form.List name={[...this.ADOPTION_PATH_MESSAGE_REMOTE]}>
              {(fields, { add, remove }) => (
                <>
                  {
                    fields.map(field => {
                      const language = form.getFieldValue([...this.ADOPTION_PATH_MESSAGE_REMOTE, field.name, 'language'])

                      return (
                        <div key={field.key} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end'
                        }}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'value']}
                            rules={[
                              {
                                required: true,
                                message: 'Required field'
                              }
                            ]}
                            style={{
                              width: '100%',
                              marginBottom: 0
                            }}
                          >
                            <Input disabled={!language} addonAfter={this.selectLanguage(field.name, this.ADOPTION_PATH_MESSAGE_REMOTE)} />
                          </Form.Item>

                          <Button type="link" onClick={() => {
                            remove(field.name)

                            this.removeLanguage('remote', language)
                          }}>
                            Remove
                          </Button>
                        </div>
                      )
                    })
                  }
                  <Button type="link" onClick={add}>
                    Add translation
                  </Button>
                </>
              )}
            </Form.List>

            <Divider />

            <Form.Item
              label="Adoption Path value set to In Person"
              name={['shelter_setting', 'adoption_path', 'in_person', 'message']}
            >
              <Input addonAfter="Default Language" />
            </Form.Item>

            <Form.List name={[...this.ADOPTION_PATH_MESSAGE_IN_PERSON]}>
              {(fields, { add, remove }) => (
                <>
                  {
                    fields.map(field => {
                      const language = form.getFieldValue([...this.ADOPTION_PATH_MESSAGE_IN_PERSON, field.name, 'language'])

                      return (
                        <div key={field.key} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end'
                        }}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'value']}
                            rules={[
                              {
                                required: true,
                                message: 'Required field'
                              }
                            ]}
                            style={{
                              width: '100%',
                              marginBottom: 0
                            }}
                          >
                            <Input disabled={!language} addonAfter={this.selectLanguage(field.name, this.ADOPTION_PATH_MESSAGE_IN_PERSON)} />
                          </Form.Item>

                          <Button type="link" onClick={() => {
                            remove(field.name)

                            this.removeLanguage('in_person', language)
                          }}>
                            Remove
                          </Button>
                        </div>
                      )
                    })
                  }
                  <Button type="link" onClick={add}>
                    Add translation
                  </Button>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </>
    )
  }
}
